import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button, Spin, Result, Modal, message } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { HomeOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useShare, useCreateRiskCalculatorShare, RISK_CALCULATOR_CONTENT_TYPE } from '../hooks/useShare';
import ResultsHeader from './results/ResultsHeader';
import ResultsTable from './results/ResultsTable';
import ScenarioModal from './results/ScenarioModal';
import RiskSymbolsLegends from '@client/components/ui/RiskSymbolsLegends';
import { BackgroundSection } from '@client/feature/activeSubstance/partials';
import { transformApiResponseToResultRows } from '../utils/transformers';
import CalculatorBackground from '../../../../img/calculator_bg.svg';
import CalculateIcon from '../../../../img/client/icons/calculate_action.svg';

/**
 * SharedResultsPage Component
 * Displays shared risk calculation results in read-only mode
 *
 * @param {string} token - Share token from URL
 */
const SharedResultsPage = ({ token }) => {
  const { t } = useTranslation('riskCalculator');

  // Fetch share data
  const { data: share, isLoading, error } = useShare(token);

  // Handler for print button
  const handlePrint = React.useCallback(() => {
    window.print();
  }, []);

  // Handler to navigate to calculator home
  const handleGoToCalculator = React.useCallback(() => {
    window.location.href = '/calculateur-indices-risque/';
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="relative w-full min-h-screen">
        <div className="container px-4 sm:px-6 lg:px-8">
          <BackgroundSection backgroundSource={CalculatorBackground} />
          <div className="flex justify-center items-center min-h-[400px]">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    const status = error.response?.status;
    const errorCode = error.response?.data?.error;

    if (status === 404) {
      return (
        <div className="relative w-full min-h-screen">
          <div className="container px-4 sm:px-6 lg:px-8">
            <BackgroundSection backgroundSource={CalculatorBackground} />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
                <div className="filters-container">
                  <Result
                    status="404"
                    title={t('share_not_found_title')}
                    subTitle={t('share_not_found_message')}
                    extra={
                      <Button
                        type="primary"
                        icon={<HomeOutlined />}
                        onClick={handleGoToCalculator}
                        className="w-full sm:w-auto h-10 sm:h-[42px] text-sm sm:text-base"
                      >
                        {t('go_to_calculator')}
                      </Button>
                    }
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      );
    }

    if (status === 410 || errorCode === 'SHARE_EXPIRED') {
      return (
        <div className="relative w-full min-h-screen">
          <div className="container px-4 sm:px-6 lg:px-8">
            <BackgroundSection backgroundSource={CalculatorBackground} />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
                <div className="filters-container">
                  <Result
                    status="warning"
                    title={t('share_expired_title')}
                    subTitle={t('share_expired_message')}
                    extra={
                      <Button
                        type="primary"
                        icon={<HomeOutlined />}
                        onClick={handleGoToCalculator}
                        className="w-full sm:w-auto h-10 sm:h-[42px] text-sm sm:text-base"
                      >
                        {t('go_to_calculator')}
                      </Button>
                    }
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      );
    }

    // Generic error
    return (
      <div className="relative w-full min-h-screen">
        <div className="container px-4 sm:px-6 lg:px-8">
          <BackgroundSection backgroundSource={CalculatorBackground} />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
              <div className="filters-container">
                <Result
                  status="error"
                  title={t('share_error_title')}
                  subTitle={error.message || t('share_error_message')}
                  extra={
                    <Button
                      type="primary"
                      icon={<HomeOutlined />}
                      onClick={handleGoToCalculator}
                    >
                      {t('go_to_calculator')}
                    </Button>
                  }
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Validate content type
  if (share && share.content_type !== RISK_CALCULATOR_CONTENT_TYPE) {
    return (
      <div className="relative w-full min-h-screen">
        <div className="container px-4 sm:px-6 lg:px-8">
          <BackgroundSection backgroundSource={CalculatorBackground} />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
              <div className="filters-container">
                <Result
                  status="error"
                  title={t('share_invalid_type_title')}
                  subTitle={t('share_invalid_type_message')}
                  extra={
                    <Button
                      type="primary"
                      icon={<HomeOutlined />}
                      onClick={handleGoToCalculator}
                    >
                      {t('go_to_calculator')}
                    </Button>
                  }
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Extract initial results and enriched data from share
  const initialResults = share?.data?.results_data || [];
  const enrichedData = share?.data?.enriched_data || null;

  // Render the interactive results component if we have data
  if (!initialResults || initialResults.length === 0) {
    return (
      <div className="relative w-full min-h-screen">
        <div className="container px-4 sm:px-6 lg:px-8">
          <BackgroundSection backgroundSource={CalculatorBackground} />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
              <div className="filters-container">
                <Result
                  status="info"
                  title={t('share_no_results_title')}
                  subTitle={t('share_no_results_message')}
                  extra={
                    <Button
                      type="primary"
                      icon={<HomeOutlined />}
                      onClick={handleGoToCalculator}
                    >
                      {t('go_to_calculator')}
                    </Button>
                  }
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Render interactive shared results
  return (
    <SharedResultsInteractive
      initialResults={initialResults}
      enrichedData={enrichedData}
      shareTitle={share?.title}
      onPrint={handlePrint}
      onGoToCalculator={handleGoToCalculator}
    />
  );
};

/**
 * Interactive shared results component
 * Allows editing, duplicating, deleting, and adding scenarios
 */
const SharedResultsInteractive = ({
  initialResults,
  enrichedData,
  shareTitle,
  onPrint,
  onGoToCalculator,
}) => {
  const { t } = useTranslation('riskCalculator');

  // Share creation mutation
  const { mutateAsync: createShare } = useCreateRiskCalculatorShare();

  // Local state for results (can be modified)
  const [results, setResults] = useState(initialResults);

  // Modal state for edit/add/duplicate scenarios
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'add',
    editingRowId: null,
    initialData: null,
  });

  // Handler for share button - create new share with current results
  const handleShare = useCallback(async () => {
    try {
      const share = await createShare({
        inputData: null,
        resultsData: results,
        enrichedData: enrichedData,
      });

      const shareUrl = `${window.location.origin}/calculateur-indices-risque/s/${share.token}/`;
      await navigator.clipboard.writeText(shareUrl);
      message.success(t('share_created_copied'));
    } catch (error) {
      console.error('Failed to create share:', error);
      message.error(t('share_error'));
    }
  }, [createShare, results, enrichedData, t]);

  // Handler for reset - go back to original shared results
  const handleReset = useCallback(() => {
    Modal.confirm({
      title: t('confirm_reset_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('confirm_reset_message'),
      okText: t('reset_confirm_ok'),
      cancelText: t('reset_confirm_cancel'),
      okButtonProps: { danger: true },
      onOk() {
        setResults(initialResults);
        message.success(t('reset_confirm_title'));
      },
    });
  }, [initialResults, t]);

  // Handler for add scenario button
  const handleAddScenario = useCallback(() => {
    setModalState({
      isOpen: true,
      mode: 'add',
      editingRowId: null,
      initialData: null,
    });
  }, []);

  // Handler for modal close
  const handleModalClose = useCallback(() => {
    setModalState({
      isOpen: false,
      mode: 'add',
      editingRowId: null,
      initialData: null,
    });
  }, []);

  // Handler for modal submit
  const handleModalSubmit = useCallback(
    (apiData, selections, modalEnrichedData, mode) => {
      const newResultRows = transformApiResponseToResultRows(
        apiData,
        selections,
        modalEnrichedData
      );

      if (mode === 'edit') {
        const newResults = results.map((r) =>
          r.id === modalState.editingRowId ? newResultRows[0] : r
        );
        setResults(newResults);
        message.success(t('save_success'));
      } else {
        setResults([...results, ...newResultRows]);
        message.success(t('save_success'));
      }
    },
    [results, modalState.editingRowId, t]
  );

  // Handler for edit action
  const handleEdit = useCallback((row) => {
    setModalState({
      isOpen: true,
      mode: 'edit',
      editingRowId: row.id,
      initialData: row,
    });
  }, []);

  // Handler for duplicate action
  const handleDuplicate = useCallback((row) => {
    setModalState({
      isOpen: true,
      mode: 'duplicate',
      editingRowId: null,
      initialData: row,
    });
  }, []);

  // Handler for delete action
  const handleDelete = useCallback(
    (row) => {
      Modal.confirm({
        title: t('confirm_delete_title'),
        icon: <ExclamationCircleOutlined />,
        content: t('confirm_delete_message'),
        okText: t('action_delete'),
        cancelText: t('reset_confirm_cancel'),
        okButtonProps: { danger: true },
        onOk() {
          const newResults = results.filter((r) => r.id !== row.id);

          if (newResults.length === 0) {
            message.info(t('share_no_results_message'));
            setResults(initialResults);
          } else {
            setResults(newResults);
            message.success(t('action_delete'));
          }
        },
      });
    },
    [results, initialResults, t]
  );

  return (
    <div className="relative w-full min-h-screen">
      <div className="container px-4 sm:px-6 lg:px-8">
        <BackgroundSection backgroundSource={CalculatorBackground} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
            <div className="filters-container">
              {/* Header with actions */}
              <ResultsHeader
                onReset={handleReset}
                onPrint={onPrint}
                onShare={handleShare}
                isSharedView={true}
                title={shareTitle || t('results_screen_title')}
              />

              {/* Instruction text and Legend section */}
              <div className="flex flex-col gap-4 mb-4">
                {/* Instruction text - full width */}
                <div className="flex flex-col items-start gap-2 p-6 rounded-2xl bg-[#FAFAFA] w-full">
                  <p className="text-xs sm:text-sm text-gray-600 m-0">
                    <Trans i18nKey="results_instruction" ns="riskCalculator" />
                  </p>
                </div>

                {/* Risk Symbols Legend */}
                <div className="flex justify-end">
                  <div className="flex flex-col justify-center items-start gap-4 py-4 px-6 rounded-2xl border border-[#F0F0F0] bg-[#FAFAFA] w-full sm:w-fit">
                    <RiskSymbolsLegends />
                  </div>
                </div>
              </div>

              {/* Results Table - fully interactive */}
              <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <ResultsTable
                  data={results}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  loading={false}
                />
              </div>

              {/* Add Scenario Button */}
              <div className="flex justify-center mt-6 px-4 sm:px-0">
                <Button
                  type="primary"
                  className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
                  icon={<img src={CalculateIcon} alt="" className="w-5 h-5" />}
                  onClick={handleAddScenario}
                >
                  {t('btn_add_scenario')}
                </Button>
              </div>

              {/* Scenario Modal */}
              <ScenarioModal
                isOpen={modalState.isOpen}
                mode={modalState.mode}
                initialData={modalState.initialData}
                parentEnrichedData={enrichedData}
                onClose={handleModalClose}
                onSubmit={handleModalSubmit}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SharedResultsPage;
